import { MapPin, Mail, Phone, MessageCircle, Clock3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/data/content";

export const Contact = () => {
  return (
    <section id="contact" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24" data-testid="contact-section">
      <div className="grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <p className="eyebrow-label" data-testid="contact-eyebrow">Contact &amp; location</p>
          <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl" data-testid="contact-heading">
            Let's talk about your child's next step.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Visit us in Duvhapark, email the academy, or send the form above — we answer every enquiry personally.
          </p>

          <div className="mt-8 space-y-4">
            <div className="flex items-start gap-4 rounded-2xl border border-border bg-white p-5 shadow-sm" data-testid="contact-address-card">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
                <MapPin className="h-5 w-5" />
              </span>
              <div>
                <p className="font-heading text-sm font-bold text-slate-900">In-person tutoring venue</p>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground" data-testid="contact-address-text">{BRAND.address}</p>
              </div>
            </div>

            <a
              href={`mailto:${BRAND.email}`}
              className="flex items-start gap-4 rounded-2xl border border-border bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
              data-testid="contact-email-card"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
                <Mail className="h-5 w-5" />
              </span>
              <div>
                <p className="font-heading text-sm font-bold text-slate-900">Email</p>
                <p className="mt-1 text-sm font-semibold text-secondary">{BRAND.email}</p>
              </div>
            </a>

            <div className="flex items-start gap-4 rounded-2xl border border-dashed border-amber-500/60 bg-amber-50 p-5" data-testid="contact-phone-card">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/15 text-amber-700">
                <Phone className="h-5 w-5" />
              </span>
              <div>
                <p className="font-heading text-sm font-bold text-slate-900">
                  Phone &amp; WhatsApp <span className="ml-1 rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">Coming soon</span>
                </p>
                <p className="mt-1 text-sm text-amber-800/80">{BRAND.phoneDisplay} — our direct number is being set up. For now, the form or email reaches us fastest.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-2xl border border-border bg-white p-5 shadow-sm" data-testid="contact-hours-card">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
                <Clock3 className="h-5 w-5" />
              </span>
              <div>
                <p className="font-heading text-sm font-bold text-slate-900">Lesson times</p>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">Weekday afternoons &amp; evenings, and Saturday mornings — scheduled around school hours.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="flex h-full min-h-[420px] flex-col justify-between overflow-hidden rounded-2xl bg-primary p-8 sm:p-12" data-testid="contact-cta-panel">
            <div>
              <p className="eyebrow-label !text-amber-400">Online or in person</p>
              <h3 className="mt-3 font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">
                Not sure which fits your child? Start with a free consultation.
              </h3>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-white/75 sm:text-base">
                Tell us the subject, the grade and where things feel stuck. We'll recommend the right tutor,
                the right group size and the right format — online from home, or at our Witbank venue.
              </p>
            </div>
            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg" className="rounded-full bg-amber-500 px-7 font-heading font-bold text-slate-900 hover:bg-amber-400" data-testid="contact-consultation-btn">
                <a href="#consultation"><MessageCircle className="mr-1.5 h-4 w-4" /> Free consultation</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full border-white/40 bg-white/10 px-7 font-heading font-bold text-white hover:bg-white/20 hover:text-white" data-testid="contact-email-btn">
                <a href={`mailto:${BRAND.email}`}><Mail className="mr-1.5 h-4 w-4" /> Email the academy</a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
